import { clsx } from "clsx";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Option } from "web/react/components/select/types";
import SizeGuide from "web/react/components/size-guide/size-guide";
import { ErrorText } from "web/react/components/size-picker/error-text";
import { SizePicker } from "web/react/components/size-picker/size-picker";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { ProductScreenSize } from "web/types/serializers";
import * as styles from "./size-area.css";

interface SizeAreaProps {
    sizes: ProductScreenSize[];
    isSizePickerExpanded: boolean;
    handleSizeLinkClick: (expanded: boolean, shouldSendAnalytic?: boolean) => void;
    isComparison?: boolean;
    isModal?: boolean;
}

export function SizeArea({
    sizes,
    isSizePickerExpanded,
    handleSizeLinkClick,
    isComparison,
    isModal,
}: SizeAreaProps): React.ReactElement | null {
    const sizeAreaRef = useRef<HTMLDivElement>(null);
    const isDeepAffiliatePhase1Enabled = environment.getFeature(
        "deep_affiliate_experiment_phase_1"
    );

    const {
        sizePicker: {
            showSizePicker,
            schemaLabels,
            userDefaultSchema,
            sizeGuideLink,
            setSelectedSizeOption,
            setError,
            setComparisonError,
            error,
            comparisonError,
        },
        activeProduct: { selectedSizeOption },
        productPurchaseType,
    } = useInStockProductPageContext();

    const isExpressCheckout = useMemo(
        () => productPurchaseType === ProductPurchaseType.EXPRESS_CHECKOUT,
        [productPurchaseType]
    );

    // scroll to the size picker error
    useEffect(() => {
        if (isComparison && comparisonError && sizeAreaRef.current) {
            sizeAreaRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [isComparison, comparisonError]);

    const sendAnalyticsOnSelect = useCallback(
        (option: Option, isComparison: boolean) => {
            if (isDeepAffiliatePhase1Enabled) {
                analytics.event("size", "clicked", undefined, false, { size: option.selectedText });
            } else {
                const action = option.disabled ? "select_oos_alert" : "select_in_stock_size";
                analytics.event(
                    isComparison ? "compare_prices_size_picker" : "size_picker",
                    action,
                    option.selectedText,
                    false,
                    null,
                    `${productPurchaseType}.size_picker.${action}`,
                    {},
                    [],
                    true
                );
            }
        },
        [productPurchaseType, isDeepAffiliatePhase1Enabled]
    );

    const handleSelect = useCallback(
        (option: Option, isComparison: boolean) => {
            setError(false);
            setComparisonError(false);
            if (isExpressCheckout) {
                analytics.event("express_checkout", "changed_size");
                return;
            }
            sendAnalyticsOnSelect(option, isComparison);
            setSelectedSizeOption(option.value);
        },
        [
            isExpressCheckout,
            sendAnalyticsOnSelect,
            setComparisonError,
            setError,
            setSelectedSizeOption,
        ]
    );

    const showSizeGuide = useMemo(
        () => !isComparison && !isExpressCheckout && !isModal && !isDeepAffiliatePhase1Enabled,
        [isComparison, isExpressCheckout, isModal, isDeepAffiliatePhase1Enabled]
    );
    const shouldShow = useMemo(
        () => showSizePicker || (!showSizePicker && !!isModal) || isDeepAffiliatePhase1Enabled,
        [isModal, showSizePicker, isDeepAffiliatePhase1Enabled]
    );

    if (!shouldShow) {
        return null;
    }

    return (
        <div
            ref={sizeAreaRef}
            className={clsx(styles.sizePicker, {
                ["hidden-tablet"]: isComparison,
                [styles.sizePickerComparison]: isComparison,
            })}
            data-testid="size-area"
        >
            {showSizeGuide && <SizeGuide sizeGuideLink={sizeGuideLink} />}
            <SizePicker
                error={isComparison ? comparisonError : error}
                onSelect={handleSelect}
                isExpanded={isSizePickerExpanded}
                schemas={schemaLabels}
                defaultSelectText={gettext("product.buy_area.select_size.label")} // "Select size to see lowest price"
                userDefaultSchema={userDefaultSchema}
                setExpanded={handleSizeLinkClick}
                sizes={sizes}
                productType={productPurchaseType}
                selectedOption={selectedSizeOption || undefined}
                isExpressCheckout={isExpressCheckout}
                isComparison={!!isComparison}
                showDisabledOptions
            />
            {isComparison ? comparisonError && <ErrorText /> : error && <ErrorText />}
        </div>
    );
}
